// src/services/_base/BaseTenantSearch.ts
import { type QuerySpec, type WhereClause } from '@/types/FirestoreQuery.ts'
import { buildServerQuery } from '@/utils/queryBuilder' // used only by explain()
import type { IFirestoreService } from '@/platform/firebase/firestore'

type OrderDir = 'asc' | 'desc'
type Session = { tenantId?: string; yardId?: string; userId?: string }

export abstract class BaseTenantSearch<T> {
  protected spec: QuerySpec<T>
  protected scopes = { yard: false, user: false }
  protected nameSuffixes: string[] = []

  constructor(
    protected readonly collection: string,
    protected readonly session: Session,
    protected nameBase: string,
    protected readonly firestore: IFirestoreService,
  ) {
    if (!session.tenantId) {
      throw new Error('tenantId is required for BaseTenantSearch')
    }

    // Set collection path to tenant subcollection
    const collectionPath = `tenants/${session.tenantId}/${collection}`

    this.spec = {
      collection: collectionPath,
      wheres: [],
      orders: [],
      meta: { name: nameBase },
    }
  }

  // --------- scope modifiers (chain any/all) ----------
  forYard(yardId?: string) {
    const id = yardId ?? this.session.yardId
    if (!id) throw new Error('yardId required for forYard()')
    this.scopes.yard = true
    this.spec.wheres!.push({ field: 'yardId', op: '==', value: id })
    this.appendToMetaName('yard')
    return this
  }

  forUser(userId?: string) {
    const id = userId ?? this.session.userId
    if (!id) throw new Error('userId required for forUser()')
    this.scopes.user = true
    this.spec.wheres!.push({ field: 'userId', op: '==', value: id })
    this.appendToMetaName('user')
    return this
  }

  byId(id: string) {
    this.where('id', '==', id)
    this.appendToMetaName('byId')
    return this
  }

  // --------- generic query utilities you can use in subclasses ----------
  protected where(field: string, op: WhereClause['op'], value: unknown) {
    this.spec.wheres!.push({ field, op, value })
    return this
  }

  protected orderBy(field: string, dir: OrderDir = 'asc') {
    this.spec.orders!.push({ field, dir })
    return this
  }

  protected limit(n: number) {
    this.spec.limit = n
    return this
  }

  protected cursor(type: 'startAt' | 'startAfter' | 'endAt' | 'endBefore', ...values: unknown[]) {
    ;(this.spec.cursors ??= []).push({ type, values })
    return this
  }

  protected appendToMetaName(suffix: string) {
    this.nameSuffixes.push(suffix)
  }

  // --------- build/run/explain ----------
  build(): QuerySpec<T> {
    // compute a composite index suggestion: [scopes...] + [order fields...]
    const fields: Array<{ field: string; dir?: OrderDir }> = []
    if (this.scopes.yard) fields.push({ field: 'yardId', dir: 'asc' })
    if (this.scopes.user) fields.push({ field: 'userId', dir: 'asc' })
    for (const o of this.spec.orders ?? []) fields.push({ field: o.field, dir: o.dir })

    const meta = { ...(this.spec.meta ?? {}) }

    // Compute final name from base + suffixes joined with '.'
    const finalName =
      this.nameSuffixes.length > 0
        ? this.nameBase + '.' + this.nameSuffixes.join('.')
        : this.nameBase
    meta.name = finalName

    if (fields.length) {
      meta.requiredIndexes = [{ fields }]
    }

    // Create defensive copy
    const builtSpec = {
      ...this.spec,
      wheres: [...(this.spec.wheres ?? [])],
      orders: [...(this.spec.orders ?? [])],
      cursors: this.spec.cursors ? [...this.spec.cursors] : undefined,
      meta,
    }

    return builtSpec
  }

  // Build and reset state for one-time use
  private buildAndReset(): QuerySpec<T> {
    const spec = this.build()

    // Reset state for reuse
    this.spec.wheres = []
    this.spec.orders = []
    this.spec.cursors = undefined
    this.scopes = { yard: false, user: false }
    this.nameSuffixes = []

    return spec
  }

  async run(): Promise<T[]> {
    const spec = this.buildAndReset()
    console.log(`🔍 Search: Running query: ${spec.meta?.name}`, {
      collection: spec.collection,
      wheres: spec.wheres,
      orders: spec.orders,
      limit: spec.limit,
      cursors: spec.cursors,
    })
    const result = await this.firestore.runQuery<T>(spec)
    console.log(`🔍 Search: Query completed: ${spec.meta?.name}, returned ${result.length} results`)
    return result
  }

  async count(): Promise<number> {
    const spec = this.buildAndReset()
    console.log(`🔍 Search: Running count: ${spec.meta?.name}`, {
      collection: spec.collection,
      wheres: spec.wheres,
      orders: spec.orders,
      limit: spec.limit,
      cursors: spec.cursors,
    })
    const result = await this.firestore.count<T>(spec)
    console.log(`🔍 Search: Count completed: ${spec.meta?.name}, returned ${result}`)
    return result
  }

  async explain(adminDb: FirebaseFirestore.Firestore, analyze = true) {
    const q = buildServerQuery<T>(adminDb, this.build())
    return q.explain({ analyze })
  }
}
